import { useState, useCallback } from "react";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useSelector } from "react-redux";

const extractCategoryList = (response) => {
  const data = response?.data?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.categories)) {
    return data.categories;
  }

  if (Array.isArray(data?.filters)) {
    return data.filters;
  }

  if (Array.isArray(response?.data?.categories)) {
    return response.data.categories;
  }

  return [];
};

const extractCollectionList = (response) => {
  const data = response?.data?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.categories)) {
    return data.categories;
  }

  if (Array.isArray(data?.collections)) {
    return data.collections;
  }

  if (Array.isArray(response?.data?.collections)) {
    return response.data.collections;
  }

  return [];
};

export const useProductUpload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const token = useSelector((state) => state.auth.token);

  const uploadProduct = useCallback(
    async (formData) => {
      setIsLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await apiHandler({
          url: endpoint.UPLOAD_PRODUCT,
          method: "POST",
          data: formData,
          authToken: { token },
          headers: {
            // Let axios set the multipart boundary for FormData.
            "Content-Type": undefined,
          },
        });

        if (response.status === 200 || response.status === 201) {
          setSuccess(true);
          setIsLoading(false);
          return { success: true, data: response.data };
        }

        const errorMessage =
          response.data?.error_message ||
          response.data?.message ||
          response.data?.error ||
          `Server error: ${response.status}`;

        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      } catch (err) {
        const errorMessage = err.message || "An error occurred during product upload";
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [token]
  );

  const updateProduct = useCallback(
    async (productId, productData) => {
      setIsLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await apiHandler({
          url: `${endpoint.UPDATE_PRODUCT || "/api/products"}/${productId}`,
          method: "PUT",
          data: productData,
          authToken: { token },
          headers: {
            // Let axios set the multipart boundary for FormData
            "Content-Type": undefined,
          },
        });

        if (response.status >= 200 && response.status < 300) {
          setSuccess(true);
          setIsLoading(false);
          return { success: true, data: response.data };
        }

        const errorMessage =
          response.data?.error_message ||
          response.data?.message ||
          response.data?.error ||
          `Server error: ${response.status}`;

        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      } catch (err) {
        const errorMessage = err.message || "An error occurred during product update";
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [token]
  );

  const fetchFilterCategories = useCallback(async (params = {}) => {
    const response = await apiHandler({
      url: endpoint.GET_FILTER_CATEGORIES,
      method: "GET",
      authToken: { token },
      params,
      noCache: true,
    });

    if (response.status !== 200) {
      const errorMessage =
        response.data?.error_message ||
        response.data?.message ||
        response.data?.error ||
        "Unable to fetch filter categories";
      return { success: false, error: errorMessage, data: [] };
    }

    return { success: true, data: extractCategoryList(response) };
  }, [token]);

  const fetchCollectionCategories = useCallback(async (params = {}) => {
    const response = await apiHandler({
      url: endpoint.GET_COLLECTION_CATEGORIES,
      method: "GET",
      authToken: { token },
      params,
      noCache: true,
    });

    if (response.status !== 200) {
      const errorMessage =
        response.data?.error_message ||
        response.data?.message ||
        response.data?.error ||
        "Unable to fetch collection categories";
      return { success: false, error: errorMessage, data: [] };
    }

    return { success: true, data: extractCollectionList(response) };
  }, [token]);

  const resetState = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    uploadProduct,
    updateProduct,
    fetchFilterCategories,
    fetchCollectionCategories,
    isLoading,
    error,
    success,
    resetState,
  };
};
